// Builds the script an admin pastes into the browser console on the IFPRI
// dashboard page. It runs inside that real browser session (so IFPRI's bot
// protection sees an ordinary visitor), fetches every group × endpoint ×
// language one at a time, and uploads the raw responses to Mahsool with the
// one-time token. The script is a single expression so it can be pasted as-is.

const PAUSE_MS = 250; // between requests, to stay gentle on IFPRI's server

export function buildUploadScript(session, apiUrl) {
  const config = {
    token: session.token,
    from: session.from,
    to: session.to,
    groups: session.groups,
    endpoints: session.endpoints,
    langs: session.langs,
    source: session.sourceBaseUrl,
    origin: new URL(session.sourceBaseUrl).origin,
    upload: `${String(apiUrl).replace(/\/+$/, "")}${session.uploadPath}`,
    pauseMs: PAUSE_MS,
  };

  return `(async (C) => {
  const log = (m, style) => console.log('%c[Mahsool] ' + m, style || '');
  if (location.origin !== C.origin) {
    throw new Error('Run this script on ' + C.origin + ' (open the IFPRI dashboard page first).');
  }
  if (C.upload.startsWith('http:')) {
    console.warn('[Mahsool] The upload address is not HTTPS (' + C.upload + '); the browser may block it from this HTTPS page.');
  }
  const total = C.groups.length * C.endpoints.length * C.langs.length;
  const responses = {};
  let done = 0;
  log('Collecting ' + total + ' datasets from IFPRI… keep this tab open.', 'font-weight:bold');
  for (const g of C.groups) {
    for (const e of C.endpoints) {
      for (const l of C.langs) {
        const url = C.source + '/' + e + '?' + new URLSearchParams({ item_group: g, from: C.from, to: C.to, lang: l });
        const r = await fetch(url, { credentials: 'include' });
        const type = r.headers.get('content-type') || '';
        if (!r.ok || !type.includes('application/json')) {
          throw new Error('IFPRI did not return data for ' + g + ' / ' + e + ' / ' + l + ' (HTTP ' + r.status + '). Reload the IFPRI page, wait for it to load fully, and run the script again.');
        }
        responses[g] = responses[g] || {};
        responses[g][e] = responses[g][e] || {};
        responses[g][e][l] = await r.json();
        done++;
        if (done % 8 === 0 || done === total) log(done + ' / ' + total);
        await new Promise((res) => setTimeout(res, C.pauseMs));
      }
    }
  }
  // gzip the upload (~2 MB of JSON shrinks ~7x) so it fits under the reverse
  // proxy's request-size limit; the server inflates it transparently.
  const json = JSON.stringify({ token: C.token, responses });
  const headers = { 'Content-Type': 'application/json' };
  let body = json;
  if (typeof CompressionStream === 'function') {
    const cs = new CompressionStream('gzip');
    const writer = cs.writable.getWriter();
    writer.write(new TextEncoder().encode(json));
    writer.close();
    const chunks = [];
    const reader = cs.readable.getReader();
    for (;;) {
      const { done: end, value } = await reader.read();
      if (end) break;
      chunks.push(value);
    }
    body = new Blob(chunks);
    headers['Content-Encoding'] = 'gzip';
  }
  const size = typeof body === 'string' ? json.length : body.size;
  log('Uploading ' + Math.round(size / 1024) + ' KB to Mahsool…', 'font-weight:bold');
  let up;
  try {
    up = await fetch(C.upload, { method: 'POST', headers, body });
  } catch (err) {
    throw new Error('Could not reach Mahsool at ' + C.upload + '. The request was blocked or rejected before reaching the server (network, security policy, or the upload is larger than the server allows). Details: ' + err.message);
  }
  const result = await up.json().catch(() => ({}));
  if (!up.ok) {
    throw new Error('Mahsool rejected the upload: ' + (result.message || ('HTTP ' + up.status)));
  }
  if (result.status === 'success') {
    log('✔ Done! The data is live. You can close this tab and return to the Mahsool dashboard.', 'color:#15803d;font-weight:bold');
  } else {
    throw new Error('Mahsool checked the data and refused it: ' + (result.error || 'unknown reason'));
  }
})(${JSON.stringify(config)}).catch((err) => console.error('[Mahsool] ✖ ' + err.message))`;
}
